import React, { useState } from "react";
import emailjs from "emailjs-com";

import usePageSeo from "../hooks/usePageSeo";
const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap');

.contact-page {
  background: #f9f7f4;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
}

/* ─── Hero ─── */
.contact-hero {
  background: #0f2e1d;
  padding: 72px 24px 64px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.contact-hero::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse at 30% 50%, rgba(26,92,56,0.5) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 50%, rgba(45,122,79,0.25) 0%, transparent 60%);
  pointer-events: none;
}
.contact-hero-inner { max-width: 560px; margin: 0 auto; position: relative; z-index: 1; }
.contact-hero-label {
  display: inline-block;
  background: rgba(255,255,255,0.1); color: #9ed4b4;
  font-size: 0.74rem; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase; padding: 5px 14px; border-radius: 100px;
  margin-bottom: 18px; border: 1px solid rgba(255,255,255,0.15);
}
.contact-hero-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700; color: #fff; line-height: 1.2; margin-bottom: 14px;
}
.contact-hero-subtitle {
  font-size: 0.96rem; color: #8aab97; line-height: 1.6;
}

/* ─── Main layout ─── */
.contact-body {
  max-width: 1100px;
  margin: 0 auto;
  padding: 64px 24px 80px;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 36px;
  align-items: start;
}

/* ─── Info card ─── */
.contact-info-card {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e8e4df;
  padding: 32px 28px;
  position: sticky;
  top: 88px;
}
.contact-info-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem; font-weight: 700; color: #1a1a1a; margin-bottom: 6px;
}
.contact-info-sub { font-size: 0.87rem; color: #8a8a8a; margin-bottom: 28px; line-height: 1.5; }

.contact-info-item {
  display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px;
}
.contact-info-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: #e8f3ed; display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; flex-shrink: 0;
}
.contact-info-label {
  font-size: 0.74rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: #1a5c38; margin-bottom: 3px; display: block;
}
.contact-info-value { font-size: 0.9rem; color: #1a1a1a; font-weight: 500; line-height: 1.4; }

.contact-divider { height: 1px; background: #e8e4df; margin: 24px 0; }

.contact-socials { display: flex; gap: 10px; }
.contact-social-btn {
  width: 38px; height: 38px; border-radius: 10px;
  border: 1px solid #e8e4df; background: none; color: #4a4a4a;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem; cursor: pointer; text-decoration: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.contact-social-btn:hover { background: #1a5c38; color: #fff; border-color: #1a5c38; }

/* ─── Form card ─── */
.contact-form-card {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e8e4df;
  padding: 36px 32px;
}
.contact-form-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem; font-weight: 700; color: #1a1a1a; margin-bottom: 6px;
}
.contact-form-sub { font-size: 0.87rem; color: #8a8a8a; margin-bottom: 28px; }

.cf-label {
  display: block; font-size: 0.76rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: #1a5c38; margin-bottom: 6px;
}
.cf-input, .cf-textarea {
  width: 100%; border: 1.5px solid #e8e4df; border-radius: 11px;
  padding: 11px 14px; font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem; color: #1a1a1a; background: #fff; outline: none;
  transition: border-color 0.18s; box-sizing: border-box; margin-bottom: 18px;
}
.cf-input:focus, .cf-textarea:focus { border-color: #1a5c38; }
.cf-input::placeholder, .cf-textarea::placeholder { color: #bbb; }
.cf-textarea { resize: vertical; min-height: 130px; }

.cf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.cf-submit {
  width: 100%; background: #1a5c38; color: #fff; border: none;
  border-radius: 12px; padding: 14px;
  font-family: 'DM Sans', sans-serif; font-size: 0.96rem; font-weight: 700;
  cursor: pointer; transition: background 0.18s, transform 0.14s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.cf-submit:hover:not(:disabled) { background: #134429; transform: translateY(-1px); }
.cf-submit:disabled { background: #a0c4b0; cursor: not-allowed; }
.cf-spinner {
  width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
  border-radius: 50%; animation: cfspin 0.7s linear infinite;
}
@keyframes cfspin { to { transform: rotate(360deg); } }

.cf-success {
  display: flex; align-items: center; gap: 10px;
  background: #e8f3ed; border: 1px solid #b8dfc8;
  border-radius: 11px; padding: 12px 16px;
  font-size: 0.88rem; color: #1a5c38; font-weight: 600;
  margin-top: 16px;
}
.cf-fail {
  display: flex; align-items: center; gap: 10px;
  background: #fef2f2; border: 1px solid #fcd5d5;
  border-radius: 11px; padding: 12px 16px;
  font-size: 0.88rem; color: #d94f3d; font-weight: 600;
  margin-top: 16px;
}

/* ─── Responsive ─── */
@media (max-width: 860px) {
  .contact-body { grid-template-columns: 1fr; }
  .contact-info-card { position: static; }
}
@media (max-width: 500px) {
  .contact-form-card { padding: 24px 18px; }
  .contact-info-card { padding: 24px 18px; }
  .cf-row { grid-template-columns: 1fr; gap: 0; }
  .contact-body { padding: 36px 16px 60px; }
}
`;

const ContactUs = () => {
  usePageSeo({
    title: "Contact FMart Korea | Halal Food Delivery Support",
    description: "Get in touch with FMart Korea — questions about halal products, orders or delivery anywhere in South Korea. Phone, email and our Yangju store address.",
  });

  const [formData, setFormData]       = useState({ name: "", email: "", subject: "", message: "" });
  const [status,   setStatus]         = useState(null); // null | 'success' | 'error'
  const [loading,  setLoading]        = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    emailjs.send(
      "your_service_id",
      "your_template_id",
      {
        from_name:  formData.name,
        from_email: formData.email,
        subject:    formData.subject,
        message:    formData.message,
        to_email:   "ahsantayyab50999@gmail.com",
      },
      "your_public_key"
    )
    .then(() => {
      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    })
    .catch(() => {
      setStatus("error");
    })
    .finally(() => {
      setLoading(false);
    });
  };

  return (
    <>
      <style>{S}</style>
      <div className="contact-page">

        {/* ─── Hero ─── */}
        <section className="contact-hero">
          <div className="contact-hero-inner">
            <span className="contact-hero-label">Get in Touch</span>
            <h1 className="contact-hero-title">We'd love to hear from you</h1>
            <p className="contact-hero-subtitle">
              Have a question about an order, a product, or just want to say hello?
              Our team is here to help.
            </p>
          </div>
        </section>

        {/* ─── Body ─── */}
        <div className="contact-body">

          {/* ─── Info ─── */}
          <div className="contact-info-card">
            <div className="contact-info-title">Contact Information</div>
            <div className="contact-info-sub">
              Reach us through any of the channels below — we usually respond within a few hours.
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">📞</div>
              <div>
                <span className="contact-info-label">Phone & WhatsApp</span>
                <div className="contact-info-value">+82-10-8385-1214</div>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">✉️</div>
              <div>
                <span className="contact-info-label">Email</span>
                <div className="contact-info-value">admin@fmartskorea.com</div>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">📍</div>
              <div>
                <span className="contact-info-label">Address</span>
                <div className="contact-info-value">경기도 양주시 광적면 가래비길 51, 1층 102호</div>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon">🕐</div>
              <div>
                <span className="contact-info-label">Working Hours</span>
                <div className="contact-info-value">Mon – Sun: 10:00 AM – 10:00 PM</div>
              </div>
            </div>

            <div className="contact-divider" />

            <div style={{ fontSize: "0.82rem", color: "#8a8a8a", marginBottom: "14px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Follow Us
            </div>
            <div className="contact-socials">
              <a href="https://www.instagram.com/fmartskorea/" target="_blank" rel="noopener noreferrer" className="contact-social-btn" aria-label="Instagram">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/share/18E6LHWSCe/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="contact-social-btn" aria-label="Facebook">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://wa.me/821083851214" className="contact-social-btn" aria-label="WhatsApp">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* ─── Form ─── */}
          <div className="contact-form-card">
            <div className="contact-form-title">Send us a message</div>
            <div className="contact-form-sub">Fill in the form below and we'll get back to you as soon as possible.</div>

            <form onSubmit={sendEmail}>
              <div className="cf-row">
                <div>
                  <span className="cf-label">Your Name</span>
                  <input className="cf-input" type="text" name="name"
                    value={formData.name} onChange={handleChange}
                    placeholder="Full name" required />
                </div>
                <div>
                  <span className="cf-label">Email Address</span>
                  <input className="cf-input" type="email" name="email"
                    value={formData.email} onChange={handleChange}
                    placeholder="your@email.com" required />
                </div>
              </div>

              <span className="cf-label">Subject</span>
              <input className="cf-input" type="text" name="subject"
                value={formData.subject} onChange={handleChange}
                placeholder="What is this about?" required />

              <span className="cf-label">Message</span>
              <textarea className="cf-textarea" name="message"
                value={formData.message} onChange={handleChange}
                placeholder="Write your message here..." rows={5} required />

              <button type="submit" className="cf-submit" disabled={loading}>
                {loading
                  ? <><div className="cf-spinner" /> Sending...</>
                  : "Send Message →"
                }
              </button>

              {status === "success" && (
                <div className="cf-success">
                  <span>✓</span>
                  Message sent! We'll get back to you soon.
                </div>
              )}
              {status === "error" && (
                <div className="cf-fail">
                  <span>✕</span>
                  Failed to send. Please try again or contact us via WhatsApp.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;